import React, { useState } from 'react';
import { Box, Card, CardContent, TextField, Button, Typography, Alert, Container, Paper } from '@mui/material';

const ForgotPassword = () => {
  const [emailOrUsername, setEmailOrUsername] = useState('');
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1=request, 2=confirm

  const handleRequest = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/reset-request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ emailOrUsername })
      });
      const data = await res.json();
      if (res.ok) {
        setMessage('If the account exists, a reset token is generated.');
        // For development, server returns token; show it
        if (data.token) {
          setToken(data.token);
        }
        setStep(2);
      } else {
        setError(data.error || 'Reset request failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');
    setLoading(true);
    try {
      const res = await fetch('/api/auth/reset-confirm', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, newPassword })
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setMessage('Password updated successfully. You can now sign in.');
      } else {
        setError(data.error || 'Reset failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Paper elevation={3} sx={{ width: '100%', maxWidth: 480 }}>
          <Card>
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography variant="h4" component="h1" gutterBottom>
                  Reset Password
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  Enter your email or username to request a reset.
                </Typography>
              </Box>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

              {step === 1 && (
                <Box component="form" onSubmit={handleRequest}>
                  <TextField
                    fullWidth
                    label="Email or Username"
                    value={emailOrUsername}
                    onChange={(e) => setEmailOrUsername(e.target.value)}
                    margin="normal"
                    required
                  />
                  <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }} disabled={loading}>
                    {loading ? 'Requesting...' : 'Request Reset'}
                  </Button>
                </Box>
              )}

              {step === 2 && (
                <Box component="form" onSubmit={handleConfirm}>
                  <TextField
                    fullWidth
                    label="Reset Token"
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    margin="normal"
                    required
                  />
                  <TextField
                    fullWidth
                    label="New Password"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    margin="normal"
                    required
                  />
                  <Button type="submit" fullWidth variant="contained" sx={{ mt: 2 }} disabled={loading}>
                    {loading ? 'Resetting...' : 'Reset Password'}
                  </Button>
                </Box>
              )}

              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  Remembered your password? <a href="/">Sign In</a>
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Paper>
      </Box>
    </Container>
  );
};

export default ForgotPassword;